import {
  createRoute,
  formatInteger,
  toFixedWithoutPadding,
} from '@backoffice-frontend/common'
import { useErrorHandlingSubscription } from '@backoffice-frontend/data-fetching'
import { TelematicDataValue } from '@backoffice-frontend/fleet-maps-common'
import { Divider, useTheme } from '@backoffice-frontend/patterns'
import { VehicleAreaRoutes } from '@backoffice-frontend/vehicle'
import {
  BatteryChargingIcon,
  BoltIcon,
  CarDoorIcon,
  ChargingStationIcon,
  CounterIcon,
  LockIcon,
  LockOpenIcon,
  PowerIcon,
  ThermometerIcon,
} from '@moia-dev/pace-icons'
import { Button, Card, Heading } from '@moia-dev/pace-web'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useFccAdTelematicsCardTelemetrySubscription } from './AdTelematicsCard.hook'

export const AdTelematicsCard = ({ vehicleId }: { vehicleId: string }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const vehicleDetailUrl = createRoute(VehicleAreaRoutes.Overview.route, {
    vehicleId,
  })

  const telemetrySubscription = useErrorHandlingSubscription(
    useFccAdTelematicsCardTelemetrySubscription,
    {
      variables: { vehicleId },
      fetchPolicy: 'no-cache',
      onError: e => {
        console.error('Error in subscription', e)
      },
    },
  )

  const telemetry = telemetrySubscription.data?.fccVehicleTelemetry

  return (
    <Card outline padding="none" cornerRadius="medium">
      <Heading
        size={'md'}
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: `${theme.space.Space2} ${theme.space.Space4}`,
        }}
      >
        {t('adFleetMap_telemetryCard_header')}
        <Link
          aria-label={t('common_details_button')}
          to={{ pathname: vehicleDetailUrl }}
          target={'_blank'}
        >
          <Button
            label={t('common_details_button')}
            size="compact"
            variant="plain"
          />
        </Link>
      </Heading>
      <Divider />
      <div
        css={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: theme.space.Space1,
          rowGap: theme.space.Space4,
          padding: theme.space.Space4,
        }}
      >
        <TelematicDataValue
          icon={telemetry?.centralLockLocked ? <LockIcon /> : <LockOpenIcon />}
          value={
            telemetry?.centralLockLocked != null
              ? telemetry.centralLockLocked
                ? t('common_locked_label_label')
                : t('common_unlocked_label')
              : t('common_notApplicable_label')
          }
          ariaLabel={t('adFleetMap_telemetryCard_centralLock_label')}
        />
        <TelematicDataValue
          icon={<CarDoorIcon />}
          value={
            telemetry?.rightSlidingDoorOpen != null
              ? telemetry.rightSlidingDoorOpen
                ? t('common_open_label')
                : t('common_closed_label')
              : t('common_notApplicable_label')
          }
          ariaLabel={t('adFleetMap_telemetryCard_doorState_label')}
        />
        <TelematicDataValue
          icon={<PowerIcon />}
          value={
            telemetry?.ignitionOn != null
              ? telemetry.ignitionOn
                ? t('common_on_label')
                : t('common_off_label')
              : t('common_notApplicable_label')
          }
          ariaLabel={t('adFleetMap_telemetryCard_ignition_label')}
        />
        <TelematicDataValue
          icon={<BatteryChargingIcon />}
          value={telemetry?.stateOfChargePercentage?.toFixed()}
          unit="%"
          ariaLabel={t('adFleetMap_telemetryCard_stateOfCharge_label')}
        />
        <TelematicDataValue
          icon={<ChargingStationIcon />}
          value={telemetry?.remainingCruisingRangeKilometer?.toFixed()}
          unit=" km"
          ariaLabel={t('adFleetMap_telemetryCard_cruisingRange_label')}
        />
        <TelematicDataValue
          icon={<BoltIcon />}
          value={
            typeof telemetry?.consumedEnergy === 'number'
              ? toFixedWithoutPadding(
                  telemetry.consumedEnergy / (3.6 * 10 ** 6),
                  2,
                )
              : undefined
          }
          unit=" kWh"
          ariaLabel={t('adFleetMap_telemetryCard_consumedEnergy_label')}
        />
        <TelematicDataValue
          icon={<CounterIcon />}
          value={
            telemetry?.mileageKilometer != null
              ? formatInteger(telemetry.mileageKilometer)
              : undefined
          }
          unit=" km"
          ariaLabel={t('adFleetMap_telemetryCard_mileage_label')}
        />
        <TelematicDataValue
          icon={<ThermometerIcon />}
          value={
            typeof telemetry?.interiorTemperatureCelsius === 'number'
              ? toFixedWithoutPadding(telemetry.interiorTemperatureCelsius, 1)
              : undefined
          }
          unit="° C"
          ariaLabel={t('adFleetMap_telemetryCard_interiorTemperature_label')}
        />
      </div>
    </Card>
  )
}
